import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription, forkJoin } from 'rxjs';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { cn } from '../../../../../utils/utils';
import { IContact } from '../../../../types/contactModule';
import { EditContactModalComponent } from '../../../forms/edit-contact-modal/edit-contact-modal.component';
import { ContactAvatarComponent } from '../../../ui/contact-avatar/contact-avatar.component';
import { StatusComponent } from '../../../ui/status/status.component';
import { SmsTextComponent } from '../../../messages/sms-text/sms-text.component';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { ContactsService, IContactMessage } from '../../../../services/contacts/contacts.service';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { contactName, humanizeSource, readPhone } from '../../../tables/contact-table/contact-row';

/** Receipt states that mean the SMS will not arrive (same set as DeliveryService). */
const FAILED_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];

interface MessageLine {
  key: string;
  when: string;
  copy: string;
  statusText: string;
  statusTone: string;
}

/**
 * The contact side panel: who they are, where they belong, and the SMS they
 * were sent recently. Also used for rows of a CSV import (`isLocalContacts`),
 * which have no history and cannot be messaged yet.
 */
@Component({
  selector: 'contact-detail',
  standalone: true,
  imports: [
    ModalComponent,
    EditContactModalComponent,
    ContactAvatarComponent,
    StatusComponent,
    SmsTextComponent,
    HasPermissionDirective,
  ],
  templateUrl: './contact-detail.component.html',
  styleUrl: './contact-detail.component.css',
})
export class ContactDetailComponent implements OnChanges {
  router = inject(Router);
  private contactsService = inject(ContactsService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @Input({ required: true }) contact: IContact | null = null;
  @Input() isLocalContacts: boolean = false; //this is to handle to the csv upload contacts
  @Output() closeModal = new EventEmitter();
  merge = cn;

  localContact: boolean = false; //controls whether the panel shows history and actions (not for CSV rows)

  showEditContactModal: boolean = false;

  // Recent messages
  messages: MessageLine[] = [];
  messagesLoading = false;
  messagesError = '';
  messageTotal = 0;
  messageFailed = 0;
  private messagesSub?: Subscription;

  readonly titleId = `cd-title-${Math.random().toString(36).slice(2, 9)}`;

  constructor() {
    this.destroyRef.onDestroy(() => this.messagesSub?.unsubscribe());
  }

  get contactHasOrganizations() {
    return !!this.contact?.companyEntities && this.contact.companyEntities.length > 0;
  }

  get contactHasTags() {
    return !!this.contact?.entityTags && this.contact.entityTags.length > 0;
  }

  get name(): string {
    return contactName(this.contact) || 'No name';
  }

  get phone(): string {
    const parsed = readPhone(this.contact);
    return parsed ? parsed.formatInternational() : (this.contact?.primaryContactNumber ?? '').toString() || '—';
  }

  get phoneIssue(): string {
    const raw = (this.contact?.primaryContactNumber ?? '').toString().trim();
    if (!raw) return 'No number';
    return readPhone(this.contact) ? '' : 'Invalid number';
  }

  get gender(): string {
    const value = (this.contact?.gender ?? '').toString().toLowerCase();
    return value ? value[0].toUpperCase() + value.slice(1) : '—';
  }

  get birthDate(): string {
    const raw = this.contact?.birthDate;
    if (!raw) return '—';
    const date = new Date(raw);
    return Number.isNaN(date.getTime())
      ? String(raw)
      : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  get place(): string {
    return [this.contact?.city, this.contact?.country].filter((part) => !!(part ?? '').toString().trim()).join(', ') || '—';
  }

  get source(): string {
    return humanizeSource(this.contact?.origin);
  }

  get added(): string {
    return formatWhen(this.contact?.createdAt);
  }

  get messageSummary(): string {
    if (!this.messageTotal) return '';
    return this.messageFailed ? `${this.sentLabel} · ${this.failedLabel}` : this.sentLabel;
  }

  /** For the status line: loading, then how many were sent, or why nothing shows. */
  get messagesStatus(): string {
    if (this.messagesLoading) return 'Loading recent messages';
    if (this.messagesError) return this.messagesError;
    if (!this.messages.length) return 'No messages sent to this contact yet.';
    return `Recent messages: ${this.messageSummary || this.sentLabel}`;
  }

  get sentLabel(): string {
    return `${formatCount(this.messageTotal)} sent`;
  }

  get failedLabel(): string {
    return `${formatCount(this.messageFailed)} failed`;
  }

  openEditContactModal() {
    this.showEditContactModal = true;
  }
  closeEditContactModal() {
    this.showEditContactModal = false;
  }

  sendMessage() {
    const params = { items: JSON.stringify([this.contact]) };
    this.router.navigate(['/organization/messages/create'], {
      queryParams: params,
    });
  }

  close() {
    this.closeModal.emit();
  }

  retryMessages() {
    this.loadMessages();
  }

  private loadMessages() {
    this.messagesSub?.unsubscribe();
    const uuid = this.contact?.uuid;
    this.messages = [];
    this.messageTotal = 0;
    this.messageFailed = 0;
    this.messagesError = '';
    if (!this.isBrowser || this.localContact || !uuid) {
      this.messagesLoading = false;
      return;
    }
    this.messagesLoading = true;
    this.messagesSub = forkJoin({
      rows: this.contactsService.fetchContactMessages(uuid, 5),
      total: this.contactsService.countContactMessages(uuid),
      failed: this.contactsService.countContactMessages(uuid, FAILED_STATES),
    }).subscribe({
      next: ({ rows, total, failed }) => {
        this.messages = (rows ?? []).map((row, index) => this.toLine(row, index));
        this.messageTotal = Math.max(total, this.messages.length);
        this.messageFailed = failed;
        this.messagesLoading = false;
      },
      error: () => {
        this.messagesLoading = false;
        this.messagesError = 'Messages did not load. Check your connection and try again.';
      },
    });
  }

  private toLine(row: IContactMessage, index: number): MessageLine {
    const state = (row.deliveryStatus ?? row.status ?? '').toString().toUpperCase();
    let statusText = 'Sending';
    let statusTone = 'pending';
    if (state === 'DELIVERED') {
      statusText = 'Delivered';
      statusTone = 'delivered';
    } else if (FAILED_STATES.includes(state)) {
      statusText = 'Failed';
      statusTone = 'failed';
    } else if (!state) {
      statusText = 'No receipt yet';
      statusTone = 'queued';
    }
    return {
      key: row.uuid ?? `${row.messageUuid ?? 'm'}-${index}`,
      when: formatWhen(row.timeStamp ?? row.createdAt ?? null),
      copy: (row.content ?? '').toString(),
      statusText,
      statusTone,
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    this.localContact = this.isLocalContacts;
    if (changes['contact'] || changes['isLocalContacts']) {
      this.loadMessages();
    }
  }
}
